import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { fmtTL, MONTHS_TR, billingStatusMeta, fmtDateTR } from "@/lib/finance";
import { Calendar, Wallet, TrendingUp, Gift, Info, History, X, Save } from "lucide-react";
import { toast } from "sonner";

const YEARS = () => { const y = new Date().getFullYear(); return [y - 2, y - 1, y, y + 1]; };

export default function FirmBilling() {
  const { workspace } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const [firms, setFirms] = useState([]);
  const [brand, setBrand] = useState(params.get("brand") || "");
  const [year, setYear] = useState(parseInt(params.get("year") || new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [bf, setBf] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() - 1, amount: "", payment_day: 1 });
  const [bfBusy, setBfBusy] = useState(false);

  const runBackfill = async () => {
    setBfBusy(true);
    try {
      const now = new Date();
      await api.post("/finance/firm-billing/backfill", {
        workspace, brand_name: brand,
        start_year: bf.year, start_month: bf.month,
        end_year: now.getFullYear(), end_month: now.getMonth() + 1,
        amount: parseFloat(bf.amount), payment_day: bf.payment_day,
      });
      toast.success("Geçmiş kayıtlar eklendi");
      setBackfillOpen(false);
      // reload
      const { data: d } = await api.get("/finance/firm-billing", { params: { workspace, brand_name: brand, year } });
      setData(d);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBfBusy(false); }
  };

  const editAmount = async (row, newAmount) => {
    const v = parseFloat(newAmount);
    if (!Number.isFinite(v) || v === row.amount) return;
    try {
      await api.put(`/finance/monthly-billing/${row.id}`, { amount: v });
      const { data: d } = await api.get("/finance/firm-billing", { params: { workspace, brand_name: brand, year } });
      setData(d);
      toast.success("Tutar güncellendi");
    } catch (err) { toast.error("Güncellenemedi"); }
  };

  const editFixed = async (row, newVal) => {
    const v = parseFloat(newVal);
    if (!Number.isFinite(v) || v === row.sabit_reklam_gideri) return;
    try {
      await api.put(`/finance/monthly-billing/${row.id}`, { fixed_expense_override: v });
      const { data: d } = await api.get("/finance/firm-billing", { params: { workspace, brand_name: brand, year } });
      setData(d);
      toast.success("Sabit reklam gideri güncellendi");
    } catch (err) { toast.error("Güncellenemedi"); }
  };

  useEffect(() => { (async () => {
    const { data: fs } = await api.get("/finance/firms", { params: { workspace } });
    setFirms(fs);
    if (!brand && fs.length) setBrand(fs[0]);
  })(); }, [workspace]);

  useEffect(() => {
    if (!brand) return;
    setParams({ brand, year: String(year) });
    (async () => {
      const { data: d } = await api.get("/finance/firm-billing", { params: { workspace, brand_name: brand, year } });
      setData(d);
    })();
  }, [workspace, brand, year]);

  return (
    <FinanceLayout>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="fb-brand">{brand || "Firma seçin"}</h1>
          <p className="text-sm text-slate-500 mt-2">Aylık firma bilanço ve ödeme takibini görüntüleyin.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setBackfillOpen(true)} data-testid="fb-backfill-btn" className="rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
            <History size={14} /> Geçmiş Veri Ekle
          </button>
          <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="fb-brand-picker"
            className="h-11 min-w-[240px] rounded-lg border border-slate-200 bg-white px-4 text-sm hover:border-slate-300">
            <option value="">Firma seçin</option>
            {firms.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <div className="relative">
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} data-testid="fb-year"
              className="h-11 rounded-lg border border-slate-200 bg-white pl-10 pr-8 text-sm hover:border-slate-300 appearance-none">
              {YEARS().map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          </div>
        </div>
      </div>

      {backfillOpen && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center px-6" data-testid="backfill-dialog">
          <div className="bg-white rounded-xl max-w-[520px] w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{brand || "Firma"} — Geçmiş Veri Ekle</h3>
              <button onClick={() => setBackfillOpen(false)}><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Başlangıç ayından bu yılın sonuna kadar aylık kayıtlar oluşturulur.</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div><label className="text-xs font-semibold text-slate-600 block mb-1">Başlangıç Ayı</label>
                <div className="flex gap-2">
                  <select value={bf.month} onChange={(e) => setBf({ ...bf, month: parseInt(e.target.value) })} className="h-11 flex-1 rounded-lg border border-slate-200 px-3 text-sm" data-testid="bf-start-month">
                    {MONTHS_TR.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  <input type="number" value={bf.year} onChange={(e) => setBf({ ...bf, year: parseInt(e.target.value) })} className="h-11 w-24 rounded-lg border border-slate-200 px-3 text-sm" data-testid="bf-start-year" />
                </div>
              </div>
              <div><label className="text-xs font-semibold text-slate-600 block mb-1">Aylık Tutar (₺)</label>
                <input type="number" value={bf.amount} onChange={(e) => setBf({ ...bf, amount: e.target.value })} placeholder="0" className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm" data-testid="bf-amount" />
              </div>
              <div><label className="text-xs font-semibold text-slate-600 block mb-1">Ödeme Günü</label>
                <select value={bf.payment_day} onChange={(e) => setBf({ ...bf, payment_day: parseInt(e.target.value) })} className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm" data-testid="bf-day">
                  {Array.from({length:31},(_,i)=>i+1).map((d)=><option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setBackfillOpen(false)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">İptal</button>
              <button onClick={runBackfill} disabled={bfBusy || !brand || !bf.amount} data-testid="bf-run"
                className="rounded-full bg-emerald-800 hover:bg-emerald-700 text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                <Save size={13} /> {bfBusy ? "Ekleniyor..." : "Aylık Kayıtları Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}

      {data && (
        <>
          <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden mb-6">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/40">
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Ay</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Aylık Çalışma Bedeli</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Sabit Reklam Gideri</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Durum</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Ödeme Tarihi</th>
                </tr>
              </thead>
              <tbody>
                {data.months.length === 0 ? (
                  <tr><td colSpan={5} className="py-14 text-center text-sm text-slate-500" data-testid="fb-empty">Bu yıl için kayıt bulunmadı.</td></tr>
                ) : data.months.map((r) => {
                  const meta = billingStatusMeta(r.status);
                  const paid = r.status === "odedi";
                  return (
                    <tr key={r.id} className="border-b border-slate-50 last:border-none hover:bg-slate-50/40" data-testid={`fb-row-${r.month}`}>
                      <td className="py-4 px-6 text-sm text-slate-800 font-medium">{MONTHS_TR[r.month - 1]} {r.year}</td>
                      <td className="py-4 px-6 text-sm font-semibold text-slate-900">
                        <input type="number" step="0.01" defaultValue={r.amount} onBlur={(e) => editAmount(r, e.target.value)} data-testid={`fb-amt-${r.month}`}
                          className="w-32 h-8 rounded-md border border-transparent hover:border-slate-200 focus:border-emerald-500 focus:bg-white bg-transparent px-2 text-sm text-right focus:outline-none focus:ring-1 focus:ring-emerald-100" />
                      </td>
                      <td className="py-4 px-6 text-sm text-slate-700">
                        <input type="number" step="0.01" defaultValue={r.sabit_reklam_gideri ?? 0} onBlur={(e) => editFixed(r, e.target.value)} data-testid={`fb-fixed-${r.month}`}
                          className="w-32 h-8 rounded-md border border-transparent hover:border-slate-200 focus:border-emerald-500 focus:bg-white bg-transparent px-2 text-sm text-right focus:outline-none focus:ring-1 focus:ring-emerald-100" />
                      </td>
                      <td className="py-4 px-6">
                        <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium border ${paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : meta.chip}`}>
                          <span className={`w-2 h-2 rounded-full ${paid ? "bg-emerald-500" : meta.dot}`} />
                          {paid ? "Ödendi" : meta.label}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-sm text-slate-600">{r.paid_at ? fmtDateTR(r.paid_at) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-3 gap-5">
            <div className="rounded-2xl border border-red-100 bg-white p-6" data-testid="fb-acik">
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-red-50 text-red-500 flex items-center justify-center"><Wallet size={20} /></div>
                <Info size={14} className="text-slate-300" />
              </div>
              <div className="text-sm text-slate-600 mb-1">Açık Bakiye</div>
              <div className="text-3xl font-bold text-red-600 tracking-tight">{fmtTL(data.acik_bakiye)}</div>
              <div className="text-xs text-slate-500 mt-1">Ödenmemiş toplam bakiye</div>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-white p-6" data-testid="fb-total">
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><TrendingUp size={20} /></div>
                <Info size={14} className="text-slate-300" />
              </div>
              <div className="text-sm text-slate-600 mb-1">Total Kazanç</div>
              <div className="text-3xl font-bold text-emerald-700 tracking-tight">{fmtTL(data.total_kazanc)}</div>
              <div className="text-xs text-slate-500 mt-1">Ödenen toplam tutar</div>
            </div>
            <div className="rounded-2xl border border-sky-100 bg-white p-6" data-testid="fb-ekstra">
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center"><Gift size={20} /></div>
                <Info size={14} className="text-slate-300" />
              </div>
              <div className="text-sm text-slate-600 mb-1">Ekstra Kazançlar</div>
              <div className="text-3xl font-bold text-sky-600 tracking-tight">{fmtTL(data.ekstra_kazanclar)}</div>
              <div className="text-xs text-slate-500 mt-1">Tek seferlik gelirler</div>
            </div>
          </div>
        </>
      )}
    </FinanceLayout>
  );
}
